import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Calendar, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { hasPerm } from "@/lib/constants";
import Pagination from "@/components/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";

const EVENT_TYPES = ["STARTED", "PAUSED", "RESUMED", "PROGRESS", "COMPLETED"];

export default function TimeLogsPage() {
  const { user: me } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [form, setForm] = useState({ task_id: "", event_type: "STARTED", minutes: "", progress: "", note: "" });
  const [errors, setErrors] = useState({});

  // Filter states
  const [q, setQ] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { items: logs, total, page, pageSize, setPage, setPageSize, refresh } = usePaginatedList("/timelogs", {
    q,
    user_id: userFilter === "all" ? "" : userFilter,
    event_type: eventFilter === "all" ? "" : eventFilter,
    from_date: fromDate,
    to_date: toDate,
  });

  useEffect(() => {
    (async () => {
      try {
        const [u, t] = await Promise.all([api.get("/users/all"), api.get("/tasks?page=1&page_size=200")]);
        setUsers(u.data);
        setTasks(t.data.items || []);
      } catch {}
    })();
  }, []);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      setEditingLog(null);
      setForm({ task_id: "", event_type: "STARTED", minutes: "", progress: "", note: "" });
      setErrors({});
    }
  };

  const handleEdit = (log) => {
    setEditingLog(log);
    setForm({
      task_id: log.task_id || "",
      event_type: log.event_type || "STARTED",
      minutes: log.minutes != null ? String(log.minutes) : "",
      progress: log.progress != null ? String(log.progress) : "",
      note: log.note || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const newErrors = {};
    if (!form.task_id) {
      newErrors.task_id = "Task is required";
    }

    // Only validate minutes if we are creating a new log
    if (!editingLog) {
      if (!form.minutes) {
        newErrors.minutes = "Minutes are required";
      } else {
        const mins = Number(form.minutes);
        if (isNaN(mins) || mins <= 0) {
          newErrors.minutes = "Minutes must be a positive number";
        }
      }
    }

    if (form.progress) {
      const prog = Number(form.progress);
      if (isNaN(prog) || prog < 0 || prog > 100) {
        newErrors.progress = "Progress must be a percentage between 0 and 100";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      if (editingLog) {
        await api.patch(`/timelogs/${editingLog.id}`, {
          task_id: form.task_id,
          event_type: form.event_type,
          progress: form.progress ? Number(form.progress) : null,
          note: form.note,
        });
        toast.success("Time log updated");
      } else {
        await api.post("/timelogs", {
          task_id: form.task_id,
          event_type: form.event_type,
          minutes: form.minutes ? Number(form.minutes) : null,
          progress: form.progress ? Number(form.progress) : null,
          note: form.note,
        });
        toast.success("Time log added");
      }
      setOpen(false);
      setEditingLog(null);
      setForm({ task_id: "", event_type: "STARTED", minutes: "", progress: "", note: "" });
      setErrors({});
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetFilters = () => {
    setQ("");
    setUserFilter("all");
    setEventFilter("all");
    setFromDate("");
    setToDate("");
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";
  const taskName = (id) => tasks.find((t) => t.id === id)?.title || "—";
  const canCreate = hasPerm(me, "timelog.create");

  return (
    <div className="space-y-6" data-testid="timelogs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Time Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">Every start, pause, and finish across your tasks.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button onClick={() => setErrors({})} data-testid="new-timelog-btn">
                <Plus className="w-4 h-4 mr-1" /> Log Time
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="timelog-dialog">
              <DialogHeader>
                <DialogTitle>{editingLog ? "Edit Time Log" : "Log Time"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Task</Label>
                  <Select
                    value={form.task_id}
                    onValueChange={(v) => {
                      setForm({ ...form, task_id: v });
                      setErrors((prev) => ({ ...prev, task_id: "" }));
                    }}
                  >
                    <SelectTrigger data-testid="timelog-form-task">
                      <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent>
                      {tasks.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.task_id && <span className="text-xs text-red-500 block mt-1">{errors.task_id}</span>}
                </div>
                <div>
                  <Label>Event</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                    <SelectTrigger data-testid="timelog-form-event">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((e) => (
                        <SelectItem key={e} value={e}>
                          {e}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Minutes</Label>
                    <Input
                      type="number"
                      value={form.minutes}
                      disabled={!!editingLog}
                      onChange={(e) => {
                        setForm({ ...form, minutes: e.target.value });
                        setErrors((prev) => ({ ...prev, minutes: "" }));
                      }}
                      data-testid="timelog-form-minutes"
                    />
                    {errors.minutes && <span className="text-xs text-red-500 block mt-1">{errors.minutes}</span>}
                  </div>
                  <div>
                    <Label>Progress %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={form.progress}
                      onChange={(e) => {
                        setForm({ ...form, progress: e.target.value });
                        setErrors((prev) => ({ ...prev, progress: "" }));
                      }}
                      data-testid="timelog-form-progress"
                    />
                    {errors.progress && <span className="text-xs text-red-500 block mt-1">{errors.progress}</span>}
                  </div>
                </div>
                <div>
                  <Label>Note</Label>
                  <Textarea
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    data-testid="timelog-form-note"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={save} data-testid="timelog-form-save">
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filter Toolbar */}
      <div className="bg-card border border-border p-3.5 rounded-lg shadow-sm space-y-3">
        <div className="flex flex-wrap gap-2.5 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search time logs by note…"
              className="pl-9 h-9 text-xs"
              data-testid="timelogs-search"
            />
          </div>

          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="timelogs-filter-user">
              <SelectValue placeholder="User" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="timelogs-filter-event">
              <SelectValue placeholder="Event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {EVENT_TYPES.map((e) => (
                <SelectItem key={e} value={e}>
                  {e}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(q || userFilter !== "all" || eventFilter !== "all" || fromDate || toDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/40 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>Logged Date:</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">From:</span>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">To:</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
            />
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">S.No</th>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Task</th>
                <th className="px-4 py-3 font-semibold">Event</th>
                <th className="px-4 py-3 font-semibold">Minutes</th>
                <th className="px-4 py-3 font-semibold">Progress</th>
                <th className="px-4 py-3 font-semibold">Note</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.id} className="border-t border-border" data-testid={`timelog-row-${l.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {(page - 1) * pageSize + i + 1}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">{userName(l.user_id)}</td>
                  <td className="px-4 py-3">{taskName(l.task_id)}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{l.event_type}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono">{l.minutes ?? "—"}</td>
                  <td className="px-4 py-3 font-mono">{l.progress != null ? `${l.progress}%` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{l.note || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {(l.user_id === me.id || ["SUPER_ADMIN", "ADMIN"].includes(me.role)) && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => handleEdit(l)}
                        data-testid={`edit-timelog-${l.id}`}
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                    No logs match the current filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="timelogs-pagination"
        />
      </Card>
    </div>
  );
}
